/* eslint-disable no-console */
import { ApolloClient, ApolloLink, InMemoryCache, split, HttpLink, makeVar } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import { createClient } from 'graphql-ws';
import { onError } from '@apollo/client/link/error';
import { captureMessage, captureException } from '@sentry/react';
import { RetryLink } from '@apollo/client/link/retry';
import isNil from 'lodash/isNil';

export const APOLLO_SUBSCRIPTION_STATUSES = {
  opened: 'opened',
  connecting: 'connecting',
  connected: 'connected',
  closed: 'closed',
  error: 'error'
};
const RETRY_DELAY = 5000;
const PING_PONG_INTERVAL = 10000;
const CONNECTION_ACK_TIMEOUT = 2000;

export const reactiveVarClient = makeVar(null);
export const reactiveVarStatus = makeVar(null);
export const reactiveVarSubscription = makeVar(null);
export const reactiveVarIsReconnected = makeVar(false);

function getEventTags(errorOrCloseEvent) {
  const tags = {
    code: errorOrCloseEvent?.code,
    reason: errorOrCloseEvent?.reason,
    type: errorOrCloseEvent?.type,
    wasClean: errorOrCloseEvent?.wasClean,
    message: errorOrCloseEvent?.message,
    hasTabFocus: document.hasFocus(),
    isOnline: window.navigator.onLine
  };
  const validatedTags = omitBy(tags, isUndefined);
  return validatedTags;
}

let lastPingTime;
let connectedAt;
let timeout;

export const isWebsocketSilentlyStopped = () => {
  const lastHeartBeat = Math.max(connectedAt, lastPingTime);
  const noHeartBeatInLastMinute = !!lastHeartBeat && Date.now() - lastHeartBeat > 60_000;
  return noHeartBeatInLastMinute && reactiveVarStatus() === APOLLO_SUBSCRIPTION_STATUSES.connected;
};

export const createCacheKey = variables => {
  const result = variables.reduce((ac, [key, value]) => {
    if (!isNil(value)) {
      return `${ac}${ac.length ? '-' : ''}${key}=${value}`;
    }
    return ac;
  }, '');
  return result;
};
export const getThreadsCacheKey = variables => {
  const orderedVariables = [
    ['channelId', variables.channelId],
    ['contactId', variables.contactId],
    ['memberId', variables.memberId],
    ['threadId', variables.threadId],
    ['mentioned', variables.mentioned],
    ['hasSchedule', variables.hasSchedule],
    ['unassigned', variables.unassigned],
    ['page', variables.page],
    ['stateIn', variables.stateIn],
    ['search', variables.search],
    ['channelIds', variables.channelIds],
    ['boardIds', variables.boardIds],
    ['priorityIds', variables.priorityIds],
    ['statusIds', variables.statusIds],
    ['swarmState', variables.swarmState],
    ['sortByCreatedAt', variables.sortByCreatedAt],
    ['sortByUpdatedAt', variables.sortByUpdatedAt]
  ];
  const cacheKeyFromVariables = createCacheKey(orderedVariables);
  return `${cacheKeyFromVariables}`;
};

export const getTimeEntriesCacheKey = variables => {
  const orderedVariables = [['threadId', variables.threadId]];
  const cacheKeyFromVariables = createCacheKey(orderedVariables);
  return `${cacheKeyFromVariables}`;
};

export const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        channelAggregated: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          }
        },
        threads: {
          keyArgs: ({ filter, search, order }, { variables }) => {
            const customCacheKey = getThreadsCacheKey({
              channelId: filter.channel_id || variables.memberChannelId,
              // In cache operations, `channelId` serves as main cache key for both regular and "My Inbox" channel.
              // "My Inbox" threads are filtered by `memberId`, and but also passes `memberChannelId`.
              // To keep consistent cache key, `memberChannelId` is used as value of `channelId`,
              // And in that case `memberId` can be dropped from cache key to avoid cache key confusion.
              ...(!variables.memberChannelId && { memberId: filter.member_id }),
              stateIn: filter.state_in,
              swarmState: filter.swarm_state,
              unassigned: filter.unassigned,
              hasSchedule: filter.has_schedule,
              sortByCreatedAt: order.created_at,
              sortByUpdatedAt: order.updated_at,
              contactId: filter.contact_id,
              mentioned: filter.mentioned,
              channelIds: filter.channel_ids && filter.channel_ids.join(','),
              boardIds: filter.board_ids && filter.board_ids.join(','),
              priorityIds: filter.priority_ids && filter.priority_ids.join(','),
              statusIds: filter.status_ids && filter.status_ids.join(','),
              search,
              page: variables.page
            });
            return customCacheKey;
          },
          merge(existing = [], incoming, { args: { cursor } }) {
            // If `incoming` is an array, it indicates the cache has been updated manually.
            // In this case, return `incoming` directly.
            const isManuallyUpdatedCache = Array.isArray(incoming);
            if (isManuallyUpdatedCache) {
              return incoming;
            }

            // Cache structure for `threads` is like {cacheKey: [page1, page2, page3, ...]}.
            // Apollo Client does not have built-in support for sequential refetch (unlike react-query),
            // leading to potential stale data beyond the first page. To prevent this,
            // only the data from the refetched first page is preserved, while data from subsequent pages is discarded.
            // For details on how react-query handles sequential pagination refetching, see: https://tanstack.com/query/v3/docs/framework/react/guides/infinite-queries#what-happens-when-an-infinite-query-needs-to-be-refetched
            const isFirstPage = !cursor;
            if (isFirstPage) {
              return [incoming];
            }
            const pageIndex = existing.length;
            const copyExisting = existing.concat([]);
            copyExisting[pageIndex] = incoming;
            return copyExisting;
          }
        },
        linkedThreads: {
          keyArgs: ({ order }, { variables }) => {
            const customCacheKey = getThreadsCacheKey({
              threadId: variables.threadId,
              sortByCreatedAt: order.created_at,
              sortByUpdatedAt: order.updated_at
            });
            return customCacheKey;
          },
          merge(existing = [], incoming) {
            const isAlreadyMergedData = Array.isArray(incoming);
            if (isAlreadyMergedData) {
              return incoming;
            }
            const pageIndex = incoming.meta.offset / incoming.meta.limit;
            if (pageIndex === 0) {
              return [incoming];
            }
            const copyExisting = existing.concat([]);
            copyExisting[pageIndex] = incoming;
            return copyExisting;
          }
        },
        timeEntries: {
          keyArgs: (_, { variables }) => {
            const customCacheKey = getTimeEntriesCacheKey({
              threadId: variables.threadId
            });
            return customCacheKey;
          }
        }
      }
    }
  }
});

export const createSubscriptionLink = ({ url, getConnectionParams }) => {
  let activeSocket;
  const subscription = createClient({
    url,
    connectionParams: getConnectionParams,
    lazy: false,
    retryAttempts: Infinity,
    shouldRetry: () => true,
    retryWait: async () => {
      await new Promise(r => setTimeout(r, RETRY_DELAY));
    },
    onNonLazyError: error => {
      captureMessage(`GraphQL websocket: onNonLazyError`, {
        tags: getEventTags(error)
      });
    },
    connectionAckWaitTimeout: CONNECTION_ACK_TIMEOUT,
    keepAlive: PING_PONG_INTERVAL,
    on: {
      connecting: () => {
        clearTimeout(timeout);
        reactiveVarIsReconnected(false);
        reactiveVarStatus(APOLLO_SUBSCRIPTION_STATUSES.connecting);
      },
      opened: () => {
        reactiveVarStatus(APOLLO_SUBSCRIPTION_STATUSES.opened);
      },
      connected: ws => {
        connectedAt = Date.now();
        // assuming if there is old websocket instance, it's reconnection
        if (activeSocket) {
          reactiveVarIsReconnected(true);
        }
        activeSocket = ws;
        reactiveVarSubscription(subscription);
        reactiveVarStatus(APOLLO_SUBSCRIPTION_STATUSES.connected);
      },
      closed: () => {
        reactiveVarStatus(APOLLO_SUBSCRIPTION_STATUSES.closed);
      },
      error: () => {
        reactiveVarStatus(APOLLO_SUBSCRIPTION_STATUSES.error);
      },
      ping: received => {
        if (!received) {
          lastPingTime = Date.now();
        }
      },
      pong: received => {
        if (received) {
          clearTimeout(timeout);
        }
      }
    }
  });
  const wsLink = new GraphQLWsLink(subscription);
  return wsLink;
};

export function createApolloClient({
  threadServiceUrl,
  getConnectionParams,
  customFetch,
  shouldConfigureWsLink = true
}) {
  const errorLoggingLink = onError(
    ({ graphQLErrors, networkError, operation, response, forward }) => {
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) => {
          captureException(new Error(`GraphQL error: ${message}`), {
            extra: {
              operationName: operation.operationName,
              locations,
              path,
              response
            },
            tags: {
              service: 'thread-service'
            }
          });
        });
      }

      if (networkError) {
        captureException(new Error(`GraphQL network error: ${networkError?.message}`), {
          extra: {
            operationName: operation.operationName,
            statusCode: networkError.statusCode
          },
          tags: {
            service: 'thread-service'
          }
        });
      }
      return forward(operation);
    }
  );

  const httpLinkUrl =
    !process.env.REACT_APP_RUN_ON_LOCAL_SERVERS && threadServiceUrl
      ? `https://${threadServiceUrl}/query`
      : process.env.REACT_APP_GRAPHQL_QUERY_ENDPOINT;

  const link = new HttpLink({
    uri: httpLinkUrl,
    fetch: customFetch
  });

  let splitLink = link;
  if (shouldConfigureWsLink) {
    const wsLinkUrl =
      !process.env.REACT_APP_RUN_ON_LOCAL_SERVERS && threadServiceUrl
        ? `wss://${threadServiceUrl}/subscriptions`
        : process.env.REACT_APP_GRAPHQL_SUBSCRIPTION_ENDPOINT;
    const wsLink = createSubscriptionLink({ url: wsLinkUrl, getConnectionParams });
    splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      wsLink,
      link
    );
  }

  const retryLink = new RetryLink({
    attempts: {
      max: 5,
      retryIf: (error, { operationName, variables }) => {
        captureMessage(`GraphQL request retry: ${error?.message}`, {
          extra: {
            operationName,
            variables,
            isOnline: window.navigator.onLine
          }
        });
        return true;
      }
    },
    delay: {
      initial: 2000
    }
  });

  const extendedLink = ApolloLink.from([retryLink, errorLoggingLink, splitLink]);
  const client = new ApolloClient({
    link: extendedLink,
    cache,
    version: 1,
    name: 'Inbox'
  });
  reactiveVarClient(client);
  window.apoloClient = client;
  return client;
}
